// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from 'informed';

import moment from 'moment';

import { Button } from '../Buttons.jsx';
import ContentBox from '../ContentBox.jsx';
import Header from '../Header.jsx';
import PatientCard from '../PatientCard.jsx';
import { SearchMenuForSearch } from './SearchMenu.jsx';
import SimpleTable from '../SimpleTable.jsx';

import { houseComparator } from '../../util/searchUtil';

import styles from '../../styles/SearchPage.scss';

type Props = {
  results: Object[],
  status: string,
}

type State = {
  viewAsTable: boolean,
}

// The page is rendered at the /search endpoint. Includes the header, search
// menu, and results.
class SearchPageComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      viewAsTable: false,
    };
  }

  componentDidMount() {
    document.title = 'HFA EHR';
  }

  componentDidUpdate(prevProps: Props) {
    const resultsContainer = document.getElementById('results');
    if (resultsContainer && prevProps.results !== this.props.results) {
      resultsContainer.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  onToggleView = () => {
    this.setState(prevState => ({
      ...prevState,
      viewAsTable: !prevState.viewAsTable,
    }));
  }

  resultsAsTable = (results) => {
    const columns = [
      {
        Header: 'House',
        accessor: 'house',
        sortMethod: houseComparator,
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Surname',
        accessor: 'surname',
      },
      {
        Header: 'Sex',
        accessor: 'sex',
      },
      {
        Header: 'Age',
        accessor: 'age',
      },
    ];
    const data = results.map(
      (patient) => {
        const house = patient.houseHistory.length
          ? patient.houseHistory[0].house
          : 'N/A';
        const { dob } = patient.generalInfo;
        const now = moment();
        const years = now.diff(dob, 'years');
        const months = now.diff(dob, 'months') % 12;
        const displayAge = `${years} years, ${months} months`;
        return {
          house,
          firstName: patient.generalInfo.firstName,
          surname: patient.generalInfo.surname,
          sex: patient.generalInfo.sex,
          age: displayAge,
        };
      },
    );
    return (
      <ContentBox>
        <SimpleTable
          data={data}
          columns={columns}
          style={{}}
        />
      </ContentBox>
    );
  }

  resultsAsCards = (results) => {
    if (this.props.status === 'SUCCESS' && !results.length) {
      return <ContentBox><span className={styles.noResults}>No results</span></ContentBox>;
    }
    const resultsByHouse = new Map();
    results.forEach(
      (patient) => {
        const house = patient.houseHistory.length
          ? patient.houseHistory[0].house
          : 'N/A';
        const { dob } = patient.generalInfo;
        const now = moment();
        const years = now.diff(dob, 'years');
        const months = now.diff(dob, 'months') % 12;
        const displayAge = `${years} years, ${months} months`;
        const card = (
          <li key={patient._id} className={styles.resultCard}>
            <PatientCard
              name={`${patient.generalInfo.firstName} ${patient.generalInfo.surname}`}
              house={house}
              sex={patient.generalInfo.sex}
              age={displayAge}
              link={`/patient/${patient._id}`}
            />
          </li>
        );

        const houseList = resultsByHouse.get(house);
        if (houseList) {
          houseList.push(card);
        } else {
          resultsByHouse.set(house, [card]);
        }
      },
    );

    const houses = [...resultsByHouse.keys()].sort(houseComparator);
    const finalCardList = houses.map(house => (
      <li key={house}>
        <div className={styles.houseHeader}>{house}</div>
        <ul>
          { resultsByHouse.get(house) }
        </ul>
      </li>
    ));

    return (
      <ul>
        { finalCardList }
      </ul>
    );
  }

  render() {
    const results = this.state.viewAsTable ? this.resultsAsTable(this.props.results) : this.resultsAsCards(this.props.results);
    return (
      <div className={styles.searchPage}>
        <Header />
        <div className={styles.searchSection}>
          <div className={styles.searchMenu}>
            <div className={styles.title}>Search Patients</div>
            <Form component={SearchMenuForSearch} autoComplete='off' />
          </div>
          <div className={styles.toggleButton}>
            <Button onClick={this.onToggleView}>Toggle Card/Table View</Button>
          </div>
          <div id='results'>
            { results }
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  results: state.search.searchData,
  status: state.search.status,
});

const SearchPage = connect(
  mapStateToProps,
)(SearchPageComponent);

export default SearchPage;



// WEBPACK FOOTER //
// ./client/src/components/searchPage/SearchPage.jsx